import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { ConsentDataUI } from 'src/app/interfaces/consent.interface';
import {
	CustomerDataUI,
	JourneyMetadataUI,
	RequestValidateCustomerData,
	ValidateCustomerDataUI,
} from 'src/app/interfaces/customer-data.interface';
import {
	GenerateSendOtpUI,
	RequestGenerateSendOtp,
} from 'src/app/interfaces/generate-send-otp.interface';
import {
	InitialParametersModel,
	InitialParametersUI,
} from 'src/app/interfaces/initial-parameters.interface';
import { OtpValidationUI, RequestOtpValidation } from 'src/app/interfaces/otp-validation.interface';
import { RequestTaxFolder, ResponseTaxFolderUI } from 'src/app/interfaces/tax-folder.interface';
import { mapper } from 'src/app/utils/mapper';
import { ApiService } from '../api/api.service';
import { EncryptionService } from '../encryption/encryption.service';

@Injectable({
	providedIn: 'root',
})
export class StoreService {
	private journeyMetadataSubject = new BehaviorSubject<JourneyMetadataUI | null>(null);
	readonly journeyMetadata$ = this.journeyMetadataSubject.asObservable();

	private initialParametersSubject = new BehaviorSubject<InitialParametersUI | null>(null);
	readonly initialParameters$ = this.initialParametersSubject.asObservable();

	private customerDataSubject = new BehaviorSubject<CustomerDataUI | null>(null);
	readonly customerData$ = this.customerDataSubject.asObservable();

	private otpSubject = new BehaviorSubject<GenerateSendOtpUI | null>(null);
	readonly otp$ = this.otpSubject.asObservable();

	private consentSubject = new BehaviorSubject<ConsentDataUI | null>(null);
	readonly consent$ = this.consentSubject.asObservable();

	constructor(
		private readonly apiService: ApiService,
		private encryptionService: EncryptionService
	) {}

	get journeyMetadata(): JourneyMetadataUI | null {
		return this.journeyMetadataSubject.value;
	}

	setCustomerData(customerData: CustomerDataUI): void {
		this.customerDataSubject.next(customerData);
	}

	initialParameters(product: string): Observable<InitialParametersModel> {
		return this.apiService.initialParameters(product).pipe(
			map(resp => mapper.initialParameters(resp)),
			tap(mapped => {
				this.journeyMetadataSubject.next({ productType: mapped.simulatorType });
				this.initialParametersSubject.next(this.extractSimulatorType(mapped));
			})
		);
	}

	validateData(req: RequestValidateCustomerData): Observable<ValidateCustomerDataUI> {
		return this.apiService.validateCustomerData(req).pipe(
			map(resp => mapper.validateData(resp)),
			tap((response: ValidateCustomerDataUI) => {
				this.patchJourneyMetadata({ uuid: response.uuid, id: response.id });
			})
		);
	}

	generateConsent(rut: string): Observable<ConsentDataUI> {
		return this.apiService.consent({ rut }).pipe(
			tap(({ certificado }) => this.encryptionService.initializePublicKey(certificado)),
			map(resp => mapper.dataConsent(resp)),
			tap((mapped: ConsentDataUI) => {
				this.consentSubject.next(mapped);
			})
		);
	}

	taxFolder(req: RequestTaxFolder): Observable<ResponseTaxFolderUI> {
		return this.apiService.taxFolder(req).pipe(map(resp => mapper.taxFolder(resp)));
	}

	generateSendOtp(
		req: RequestGenerateSendOtp,
		skipLoader: boolean = false
	): Observable<GenerateSendOtpUI> {
		return this.apiService.generateAndSendOtp(req, skipLoader).pipe(
			map(resp => mapper.otpGenerated(resp)),
			tap((mapped: GenerateSendOtpUI) => {
				if (req.nuevoDestino) this.updateCustomerPhone(req.nuevoDestino);
				this.otpSubject.next(mapped);
			})
		);
	}

	validationOtp(req: RequestOtpValidation): Observable<OtpValidationUI> {
		return this.apiService.validateOtp(req).pipe(map(mapper.otpValidation));
	}

	private extractSimulatorType(parameters: InitialParametersModel): InitialParametersUI {
		const { simulatorType, ...parametersUI } = parameters;
		return parametersUI;
	}

	private updateCustomerPhone(newPhone: string): void {
		const currentCustomer = this.customerDataSubject.value;
		if (currentCustomer) {
			this.customerDataSubject.next({
				...currentCustomer,
				phoneNumber: newPhone,
			});
		}
	}

	private patchJourneyMetadata(patch: Partial<JourneyMetadataUI>): void {
		const current = this.journeyMetadataSubject.value;
		if (!current) return;

		this.journeyMetadataSubject.next({
			...current,
			...patch,
		});
	}
}
